import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { semantic, typography, radius } from '@/shared/theme/tokens';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { useSvgMarkup } from '../hooks/useSvgMarkup';
import { applyHmiTheme } from './hmiTheme';
import { liveMeta } from './liveValueMeta';
import { ZoomPanViewport } from './ZoomPanViewport';

/**
 * Renders a sanitized SVG/HMI diagram and wires its `[data-section]` regions.
 *
 * Two modes:
 *  - mode="interactive" (overview): click a bound region -> it selects, and its
 *    parameters (with latest live value) appear in a dedicated side panel.
 *    Click a param row -> onParamClick(param).
 *  - mode="config" (settings): bound regions are tinted; clicking a region calls
 *    onSectionClick(sectionId). The selected region is driven by activeSectionId.
 *
 * @param {object}   props
 * @param {object}   props.template        SvgTemplate ({ id, name, svgUrl, sections })
 * @param {object[]} [props.svgSections]   [{ sectionId, parameterIds }]
 * @param {object[]} [props.allParams]     full parameter list (for label/lookup)
 * @param {Record<string, object>} [props.liveMap]  parameterId -> live value row
 * @param {'interactive'|'config'} [props.mode]
 * @param {string}   [props.activeSectionId]  selected region in config mode
 * @param {(p:object)=>void} [props.onParamClick]
 * @param {(sectionId:string)=>void} [props.onSectionClick]
 */
export function SvgDiagram({
  template,
  svgSections = [],
  allParams = [],
  liveMap = {},
  mode = 'interactive',
  activeSectionId = null,
  onParamClick,
  onSectionClick,
}) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const { isMobile, isTablet } = useBreakpoint();
  const isNarrow = isMobile || isTablet;
  const hostRef = useRef(null);
  const { data: markup, isLoading, isError, error } = useSvgMarkup(template);

  // Internal selection for interactive mode (config mode uses activeSectionId).
  const [selectedId, setSelectedId] = useState(null);
  const selected = mode === 'config' ? activeSectionId : selectedId;

  // sectionId -> parameter objects
  const paramsBySection = useMemo(() => {
    const byId = new Map(allParams.map((p) => [p.id, p]));
    const out = {};
    for (const s of svgSections) {
      out[s.sectionId] = (s.parameterIds ?? []).map((id) => byId.get(id)).filter(Boolean);
    }
    return out;
  }, [svgSections, allParams]);

  const sectionLabels = useMemo(() => {
    const out = {};
    for (const s of template?.sections ?? []) out[s.id] = s.label;
    return out;
  }, [template]);

  // Drop selection if the section is no longer bound (template/binding changed).
  useEffect(() => {
    if (mode === 'interactive' && selectedId && !(paramsBySection[selectedId]?.length)) {
      setSelectedId(null);
    }
  }, [mode, selectedId, paramsBySection]);

  // Inline the markup once it's ready
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !markup) return;
    host.innerHTML = markup;
    const svg = host.querySelector('svg');
    if (svg) {
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.style.display = 'block';
      svg.style.maxHeight = '100%';
      applyHmiTheme(svg, isDark);
    }
  }, [markup, isDark]);

  // Wire region interactions. Re-run when bindings/mode/selection change.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !markup) return undefined;

    const regions = Array.from(host.querySelectorAll('[data-section]'));
    const cleanups = [];

    for (const el of regions) {
      const sectionId = el.getAttribute('data-section');
      const bound = (paramsBySection[sectionId]?.length ?? 0) > 0;
      const isSelected = selected === sectionId;

      el.style.cursor = (mode === 'config' || bound) ? 'pointer' : 'default';
      el.style.transition = 'opacity .15s, filter .15s';

      const applyBase = () => {
        if (isSelected) {
          el.style.opacity = '1';
          el.style.filter = `drop-shadow(0 0 7px ${semantic.brandAccent})`;
        } else if (mode === 'config' && bound) {
          el.style.opacity = '0.97';
          el.style.filter = `drop-shadow(0 0 3px ${semantic.infoCyan})`;
        } else {
          el.style.opacity = '1';
          el.style.filter = 'none';
        }
      };
      applyBase();

      const onEnter = () => {
        if (isSelected) return;
        if (mode === 'interactive' && !bound) { el.style.filter = `drop-shadow(0 0 3px ${c.textMuted})`; return; }
        el.style.filter = `drop-shadow(0 0 6px ${semantic.brandAccent})`;
      };
      const onLeave = () => { applyBase(); };
      const onClick = (e) => {
        e.stopPropagation();
        if (mode === 'config') { onSectionClick?.(sectionId); return; }
        if (bound) setSelectedId((cur) => (cur === sectionId ? null : sectionId));
      };

      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mouseleave', onLeave);
      el.addEventListener('click', onClick);
      cleanups.push(() => {
        el.removeEventListener('mouseenter', onEnter);
        el.removeEventListener('mouseleave', onLeave);
        el.removeEventListener('click', onClick);
      });
    }

    return () => cleanups.forEach((fn) => fn());
  }, [markup, paramsBySection, mode, selected, onSectionClick, c.textMuted]);

  if (isLoading) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.textSubtle, fontSize: typography.fontSize.md }}>
        Loading diagram…
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{
        height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', gap: 8, padding: 20,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 36, color: semantic.error }}>broken_image</span>
        <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>Couldn’t load this diagram’s SVG</span>
        <span style={{ fontSize: typography.fontSize.base, color: c.textSubtle, maxWidth: 420, lineHeight: 1.45 }}>
          {error?.message ?? 'The template’s SVG could not be fetched.'}
        </span>
      </div>
    );
  }

  // Config mode: just the diagram (selection panel lives in the settings editor).
  if (mode === 'config') {
    return (
      <div ref={hostRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
    );
  }

  const selectedParams = selected ? (paramsBySection[selected] ?? []) : [];

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: isNarrow ? 'column' : 'row', gap: 12, minHeight: 0 }}>
      {/* Diagram — pan + zoom (wheel/pinch to zoom, drag to pan, reset button) */}
      <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <ZoomPanViewport>
          <div ref={hostRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
        </ZoomPanViewport>
      </div>

      {/* Dedicated parameter panel */}
      <div style={{
        width: isNarrow ? '100%' : 230, flexShrink: 0, display: 'flex', flexDirection: 'column',
        borderLeft: isNarrow ? 'none' : `1px solid ${c.border}`,
        borderTop: isNarrow ? `1px solid ${c.border}` : 'none',
        paddingLeft: isNarrow ? 0 : 12, paddingTop: isNarrow ? 12 : 0,
        minHeight: isNarrow ? 120 : 0,
      }}>
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 8, color: c.textSubtle }}>
            <span className="material-symbols-outlined" style={{ fontSize: 28, color: c.textMuted }}>touch_app</span>
            <span style={{ fontSize: typography.fontSize.base, lineHeight: 1.4 }}>Click a highlighted zone to see its parameters.</span>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: semantic.brandAccent }}>my_location</span>
              <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {sectionLabels[selected] ?? selected}
              </span>
              <button
                onClick={() => setSelectedId(null)}
                title="Clear selection"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textSubtle, display: 'flex', padding: 2 }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', minHeight: 0 }}>
              {selectedParams.map((p) => {
                const live = liveMap[p.id];
                const meta = liveMeta(live);
                return (
                  <button
                    key={p.id}
                    onClick={() => onParamClick?.(p)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      padding: '8px 10px', borderRadius: radius.md, cursor: 'pointer', textAlign: 'left',
                      background: c.bg, border: `1px solid ${c.borderSubtle}`,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = c.bgSubtle; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = c.bg; }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.displayName}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
                        <span style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: meta?.color ?? c.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                          {live?.value != null ? Number(live.value).toFixed(2) : '—'}
                        </span>
                        {p.unit && <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle }}>{p.unit}</span>}
                      </div>
                    </div>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: semantic.brandAccent, flexShrink: 0 }}>show_chart</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
